import { useEffect, useMemo, useState } from "react";
import { getInfographicSection } from "../../../config/infographicTemplate";

/**
 * Infographic proposal template — built to the approved reference
 * (cre-FE/reference/reference-proposal.html).
 *
 * The palette, type and layout are the reference's own and are deliberately
 * fixed: this proposal goes out under the freelance profile the client already
 * knows from the platform, not under an agency brand, so no tenant colour,
 * logo or company name is applied to it.
 *
 * It is also always light — the reference carried a `prefers-color-scheme: dark`
 * block, but a client-facing proposal has to look the same for everybody, so
 * `color-scheme: light` is declared and the palette never switches.
 *
 * The same component renders the review preview and the published client page,
 * so what the BD signs off is exactly what the client receives.
 */

interface Props {
  proposal: any;
  /** The freelance profile this proposal is sent from — it signs the page. */
  persona?: { name?: string; profilePic?: string } | null;
  /** "preview" un-fixes the sticky bars so the page sits inside a review pane. */
  mode?: "preview" | "public";
}

// ── helpers ──────────────────────────────────────────────────────────────────
const txt = (v: any) => String(v ?? "").trim();
const plain = (v: any) =>
  txt(v).replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
const list = (v: any) => (Array.isArray(v) ? v : []);
const rowText = (row: any) =>
  typeof row === "string" ? row : txt(row?.title || row?.name || row?.label || row?.value || row?.quote || row?.text);
const rowHasText = (row: any) => !!plain(rowText(row));

/** A section is rendered only when it actually carries content. */
const sectionHasContent = (s: any) => {
  const c = s?.content;
  if (!c) return false;
  if (typeof c === "string") return !!plain(c);
  const strings = ["text", "headline", "subheadline", "callout", "note", "name", "company", "email", "phone", "website", "subtitle"];
  if (strings.some((k) => plain(c[k]))) return true;
  const lists = ["items", "points", "steps", "tiers", "projects", "bestProjects", "milestones", "pillars", "highlights", "stats", "badges", "excluded", "ledes"];
  return lists.some((k) => list(c[k]).some(rowHasText));
};

// Plain strings become paragraphs; editor HTML is rendered as-is.
const Body = ({ value, className = "" }: { value: any; className?: string }) => {
  const s = txt(value);
  if (!s) return null;
  if (/<[a-z][\s\S]*>/i.test(s)) return <div className={`pp-rich ${className}`} dangerouslySetInnerHTML={{ __html: s }} />;
  return (
    <>
      {s.split(/\n{2,}/).map((p, i) => (
        <p key={i} className={className}>{p}</p>
      ))}
    </>
  );
};

const money = (v: any, currency = "USD") => {
  const s = txt(v);
  if (!s) return "";
  if (/^[^\d]/.test(s)) return s; // already written out ("From $500", "Custom")
  const n = Number(s.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n) || !n) return s;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString()}`;
  }
};

// The context bar's pills are one or two words — the reference reads
// "Understanding · Approach · Investment · Reviews", not the full headings.
const NAV_LABEL: Record<string, string> = {
  "executive-summary": "Summary",
  understanding: "Understanding",
  "proposed-solution": "Solution",
  "scope-of-work": "Scope",
  services: "Services",
  deliverables: "Deliverables",
  approach: "Approach",
  process: "Process",
  timeline: "Timeline",
  pricing: "Investment",
  addons: "Add-ons",
  "why-us": "Why me",
  testimonials: "Reviews",
  terms: "Terms",
};

const Kicker = ({ children }: { children: any }) =>
  children ? <span className="pp-kicker">{children}</span> : null;

// "SIGNATURE" + "The recommended fit" → "Signature package". The tag is the
// package's name; the tier's own line is the description under it.
const packageLabel = (tier: any) => {
  const tag = txt(tier?.tag);
  if (tag) {
    const pretty = tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
    return `${pretty} package`;
  }
  return txt(tier?.name || tier?.title);
};

const Stars = ({ n = 5 }: { n?: number }) => (
  <div className="pp-stars">{"★".repeat(Math.max(1, Math.min(5, Number(n) || 5)))}</div>
);

const InfographicTemplate = ({ proposal, persona, mode = "public" }: Props) => {
  const p = proposal || {};
  const who = persona || p.accountPersona || null;

  const sections = useMemo(
    () =>
      list(p.sections)
        .filter((s: any) => s && !s.hidden)
        .filter(sectionHasContent)
        .slice()
        .sort((a: any, c: any) => (a.order || 0) - (c.order || 0)),
    [p.sections]
  );

  const find = (...types: string[]) => sections.find((s: any) => types.includes(s.type));
  const cover = find("cover");
  const portfolio = find("portfolio", "portfolio-grid", "recent-work");
  const pricing = find("pricing");
  const nextSteps = find("next-steps", "call-to-action", "cta");

  const clientName =
    txt(p.clientInfo?.name) || txt(p.clientInfo?.company) || txt(p.client?.name) || txt(p.client?.company) ||
    txt(find("client-info")?.content?.name) || txt(find("client-info")?.content?.company);

  // The client deals with the freelance profile this proposal was sent from —
  // that persona's name and picture carry the page, ahead of anything typed on
  // the Contact section and ahead of the BD's own account name.
  const contactContent = find("contact")?.content || {};
  const signer = who?.name || txt(contactContent.name) || txt(p.fullName);
  const credentials = txt(contactContent.note);

  const ctaLabel = txt(nextSteps?.content?.buttonText) || "Message me";
  const ctaUrl = txt(nextSteps?.content?.buttonUrl) || txt(p.hireLink);

  // The recommended pricing option drives the sticky bar's line, exactly as the
  // reference reads "Ready when you are — Signature package, $380".
  const featuredTier =
    list(pricing?.content?.tiers).find((t: any) => t?.featured) || list(pricing?.content?.tiers)[0] || null;

  // ── Section navigation pills ───────────────────────────────────────────────
  const navItems = sections.filter(
    (s: any) => !["cover", "client-info", "contact", "next-steps", "portfolio", "portfolio-grid", "recent-work"].includes(s.type)
  );
  const [active, setActive] = useState<string>("");
  const navKey = navItems.map((s: any) => s.id).join("|");
  useEffect(() => {
    if (!navItems.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, c) => a.boundingClientRect.top - c.boundingClientRect.top);
        if (vis[0]) setActive(vis[0].target.id.replace("pp-", ""));
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    navItems.forEach((s: any) => {
      const el = document.getElementById(`pp-${s.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navKey]);

  // ── Portfolio spotlight (hero aside) + modal carousel ──────────────────────
  const pf = portfolio?.content || {};
  const pfPrimary = list(pf.projects).filter((x: any) => x?.image || rowHasText(x));
  const pfBest = list(pf.bestProjects).filter((x: any) => x?.image || rowHasText(x));
  const hasTabs = pfPrimary.length > 0 && pfBest.length > 0;
  const [pfTab, setPfTab] = useState<"primary" | "best">("primary");
  const pfItems = hasTabs ? (pfTab === "primary" ? pfPrimary : pfBest) : pfPrimary.length ? pfPrimary : pfBest;
  const pfLabel =
    hasTabs
      ? pfTab === "primary"
        ? txt(pf.primaryTabLabel) || "For your industry"
        : txt(pf.bestTabLabel) || "Best of my work"
      : txt(pf.soloLabel) || "Best of my recent work";
  const pfNote =
    hasTabs && pfTab === "primary"
      ? txt(pf.note) || "Showing work most relevant to your industry by default — switch tabs to see the range."
      : txt(pf.bestNote) || txt(pf.note) || "Selected from recent completed work across industries.";

  const [modalIdx, setModalIdx] = useState<number | null>(null);
  const modalOpen = modalIdx !== null;
  const stepModal = (d: number) =>
    setModalIdx((i) => (i === null || !pfItems.length ? i : (i + d + pfItems.length) % pfItems.length));
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModalIdx(null);
      if (e.key === "ArrowRight") stepModal(1);
      if (e.key === "ArrowLeft") stepModal(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, pfItems.length]);

  const PortfolioCard = () =>
    !pfItems.length ? null : (
      <div className={`pp-card pp-portfolio-card ${hasTabs ? "" : "is-solo"}`}>
        <div className="pp-portfolio-head">
          <span>{txt(pf.subtitle) || "Recent work"}</span>
        </div>

        {hasTabs ? (
          <div className="pp-pf-tabs">
            <button className={`pp-pf-tab ${pfTab === "primary" ? "is-active" : ""}`} onClick={() => setPfTab("primary")}>
              {txt(pf.primaryTabLabel) || "For your industry"}
            </button>
            <button className={`pp-pf-tab ${pfTab === "best" ? "is-active" : ""}`} onClick={() => setPfTab("best")}>
              {txt(pf.bestTabLabel) || "Best of my work"}
            </button>
          </div>
        ) : (
          <div className="pp-pf-solo">{pfLabel}</div>
        )}

        <div className="pp-pf-panel">
          {pfItems.slice(0, 2).map((x: any, i: number) => (
            <button key={i} className="pp-pf-tile" onClick={() => setModalIdx(i)}>
              <div className="pp-pf-swatch">
                {txt(x.image) ? (
                  <img src={x.image} alt={txt(x.title)} loading="lazy" />
                ) : (
                  <span className="pp-pf-initial">{(txt(x.title) || "·").slice(0, 1)}</span>
                )}
              </div>
              <div className="pp-pf-info">
                <p className="pp-pf-name">{txt(x.title)}</p>
                {txt(x.meta || x.description) && <p className="pp-pf-meta">{txt(x.meta || x.description)}</p>}
              </div>
            </button>
          ))}
        </div>

        {pfItems.length > 2 && (
          <button className="pp-pf-seeall" onClick={() => setModalIdx(0)}>
            See all {pfItems.length} projects →
          </button>
        )}
        {pfNote && <p className="pp-pf-note">{pfNote}</p>}
      </div>
    );

  // ── Sections ───────────────────────────────────────────────────────────────
  const renderSection = (s: any) => {
    const c = s.content || {};
    const def = getInfographicSection(s.type);
    const heading = txt(s.title) || def?.title || "";
    const kicker = txt(s.subtitle);

    const wrap = (children: any, extra = "", band = false) => (
      <section key={s.id} id={`pp-${s.id}`} className={band ? "pp-band" : `pp-section ${extra}`}>
        <div className={band ? "pp-wrap pp-section" : "pp-wrap"}>{children}</div>
      </section>
    );

    switch (s.type) {
      // These carry data rather than a block of their own — the cover and the
      // portfolio are the hero, and client/contact details name the parties in
      // the footer. The reference proposal has no card for any of them.
      case "cover":
      case "portfolio":
      case "portfolio-grid":
      case "recent-work":
      case "client-info":
      case "contact":
        return null;

      // The reference's "What I'm reading in your brief" block: kicker, then a
      // card split heading-left / prose-right with an accent edge.
      case "understanding":
      case "executive-summary":
        return wrap(
          <>
            <Kicker>{kicker || (s.type === "understanding" ? "What I'm reading in your brief" : "In short")}</Kicker>
            <div className="pp-card pp-understood">
              <div><h2>{heading}</h2></div>
              <div>
                <Body value={c.text} />
                {list(c.points).filter(rowHasText).length > 0 && (
                  <ul className="pp-ticks">
                    {list(c.points).filter(rowHasText).map((x: any, i: number) => <li key={i}>{rowText(x)}</li>)}
                  </ul>
                )}
                {list(c.highlights).filter(rowHasText).length > 0 && (
                  <div className="pp-mini-stats">
                    {list(c.highlights).filter(rowHasText).map((h: any, i: number) => (
                      <div key={i}><b>{h.value}</b><span>{h.label}</span></div>
                    ))}
                  </div>
                )}
                {txt(c.callout) && <p className="pp-callout">{c.callout}</p>}
              </div>
            </div>
          </>
        );

      case "proposed-solution":
        return wrap(
          <>
            <Kicker>{kicker || "What I'd propose"}</Kicker>
            <h2>{heading}</h2>
            {plain(c.text) && <div className="pp-lede-block"><Body value={c.text} /></div>}
            {list(c.pillars).filter(rowHasText).length > 0 && (
              <ol className="pp-steps">
                {list(c.pillars).filter(rowHasText).map((x: any, i: number) => (
                  <li className="pp-card" key={i}>
                    <span className="pp-num">{i + 1}</span>
                    <h3>{x.title}</h3>
                    <p>{x.description}</p>
                  </li>
                ))}
              </ol>
            )}
          </>
        );

      case "approach":
      case "process":
        return wrap(
          <>
            <Kicker>{kicker || "How this would run"}</Kicker>
            <h2>{heading}</h2>
            <ol className="pp-steps">
              {list(c.steps).filter(rowHasText).map((x: any, i: number) => (
                <li className="pp-card" key={i}>
                  <span className="pp-num">{i + 1}</span>
                  <h3>{rowText(x)}</h3>
                  {typeof x !== "string" && txt(x.description) && <p>{x.description}</p>}
                </li>
              ))}
            </ol>
          </>
        );

      case "scope-of-work":
        return wrap(
          <>
            <Kicker>{kicker || "What's included"}</Kicker>
            <h2>{heading}</h2>
            <ol className="pp-steps">
              {list(c.items).filter(rowHasText).map((x: any, i: number) => (
                <li className="pp-card" key={i}>
                  <span className="pp-num">{i + 1}</span>
                  <h3>{rowText(x)}</h3>
                  {typeof x !== "string" && txt(x.description) && <p>{x.description}</p>}
                </li>
              ))}
            </ol>
            {list(c.excluded).filter(rowHasText).length > 0 && (
              <div className="pp-excluded">
                <span>Not included</span>
                <ul>{list(c.excluded).filter(rowHasText).map((x: any, i: number) => <li key={i}>{rowText(x)}</li>)}</ul>
              </div>
            )}
          </>
        );

      case "services":
      case "addons":
        return wrap(
          <>
            <Kicker>{kicker || (s.type === "addons" ? "Optional extras" : "Services")}</Kicker>
            <h2>{heading}</h2>
            <div className="pp-grid3">
              {list(c.items).filter(rowHasText).map((x: any, i: number) => (
                <div className="pp-card pp-simple" key={i}>
                  <h3>{x.title}</h3>
                  {txt(x.description) && <p>{x.description}</p>}
                  {txt(x.price) && <p className="pp-inline-price">{money(x.price, c.currency)}</p>}
                </div>
              ))}
            </div>
          </>
        );

      case "deliverables":
        return wrap(
          <>
            <Kicker>{kicker || "What you get"}</Kicker>
            <h2>{heading}</h2>
            <div className="pp-card pp-deliverables">
              <ul className="pp-ticks">
                {list(c.items).filter(rowHasText).map((x: any, i: number) => (
                  <li key={i}>
                    <b>{rowText(x)}</b>
                    {typeof x !== "string" && txt(x.description) && <span> — {x.description}</span>}
                    {typeof x !== "string" && txt(x.format) && <em className="pp-format">{x.format}</em>}
                  </li>
                ))}
              </ul>
            </div>
          </>
        );

      case "timeline":
        return wrap(
          <>
            <Kicker>{kicker || "Timeline"}</Kicker>
            <h2>{heading}</h2>
            <ol className="pp-steps">
              {list(c.milestones).filter(rowHasText).map((m: any, i: number) => (
                <li className="pp-card" key={i}>
                  <span className="pp-num">{i + 1}</span>
                  <h3>{m.title}</h3>
                  {(txt(m.duration) || txt(m.date)) && (
                    <p className="pp-duration">{txt(m.duration) || new Date(m.date).toLocaleDateString()}</p>
                  )}
                  {txt(m.description) && <p>{m.description}</p>}
                </li>
              ))}
            </ol>
          </>
        );

      // The reference's pricing band — soft accent background, edge to edge.
      case "pricing":
        return (
          <section key={s.id} id={`pp-${s.id}`} className="pp-pricing-band">
            <div className="pp-wrap pp-section">
              <Kicker>{kicker || "Investment · the section that matters most"}</Kicker>
              <h2>{heading}</h2>
              <div className="pp-tiers">
                {list(c.tiers).filter(rowHasText).map((t: any, i: number) => (
                  <div className={`pp-card pp-tier ${t.featured ? "is-featured" : ""}`} key={i}>
                    {t.featured && <span className="pp-flag">Recommended</span>}
                    {txt(t.tag) && <p className="pp-tier-tag">{t.tag}</p>}
                    <h3>{t.name || t.title}</h3>
                    <p className="pp-price">
                      {money(t.price, c.currency || "USD") || <span className="pp-price-tba">Price on request</span>}
                      {txt(t.price) && txt(t.unit) && <small> {t.unit}</small>}
                    </p>
                    <ul>
                      {list(t.features).filter(rowHasText).map((f: any, j: number) => <li key={j}>{rowText(f)}</li>)}
                    </ul>
                    {txt(t.note) && <p className="pp-tier-note">{t.note}</p>}
                  </div>
                ))}
              </div>
              {txt(c.note) && <p className="pp-band-note">{c.note}</p>}
            </div>
          </section>
        );

      case "why-us":
        return wrap(
          <>
            <Kicker>{kicker || "Why me"}</Kicker>
            <h2>{heading}</h2>
            {list(c.stats).filter(rowHasText).length > 0 && (
              <div className="pp-mini-stats pp-mini-stats-wide">
                {list(c.stats).filter(rowHasText).map((x: any, i: number) => (
                  <div key={i}><b>{x.value}</b><span>{x.label}</span></div>
                ))}
              </div>
            )}
            <div className="pp-grid3">
              {list(c.points).filter(rowHasText).map((x: any, i: number) => (
                <div className="pp-card pp-simple" key={i}>
                  <h3>{x.title}</h3>
                  {txt(x.description) && <p>{x.description}</p>}
                </div>
              ))}
            </div>
          </>
        );

      // The reference's star-rated client quotes.
      case "testimonials":
        return wrap(
          <>
            <Kicker>{kicker || "From recent clients"}</Kicker>
            <h2>{heading}</h2>
            <div className="pp-quotes">
              {list(c.items).filter(rowHasText).map((q: any, i: number) => (
                <div className="pp-card pp-quote" key={i}>
                  <Stars n={q.rating} />
                  <p>“{txt(q.quote || q.text || q.title)}”</p>
                  {txt(q.attribution || q.name) && (
                    <p className="pp-attr">
                      {txt(q.attribution || q.name)}
                      {txt(q.position) ? `, ${q.position}` : ""}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        );

      case "terms":
        return wrap(
          <>
            <Kicker>{kicker || "Terms"}</Kicker>
            <h2>{heading}</h2>
            {plain(c.text) && <div className="pp-lede-block"><Body value={c.text} /></div>}
            {list(c.items).filter(rowHasText).length > 0 && (
              <div className="pp-grid2">
                {list(c.items).filter(rowHasText).map((x: any, i: number) => (
                  <div className="pp-card pp-simple" key={i}>
                    <h3>{typeof x === "string" ? `Term ${i + 1}` : x.title}</h3>
                    <p>{typeof x === "string" ? x : x.text || x.description}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        );

      // The reference's closing card.
      case "next-steps":
      case "call-to-action":
      case "cta":
        return (
          <section key={s.id} id={`pp-${s.id}`} className="pp-closing">
            <div className="pp-wrap">
              <div className="pp-card">
                <h2>{heading || "If this reads right, let's make it real."}</h2>
                <Body value={c.text} />
                {list(c.steps).filter(rowHasText).length > 0 && (
                  <ol className="pp-next-list">
                    {list(c.steps).filter(rowHasText).map((x: any, i: number) => <li key={i}>{rowText(x)}</li>)}
                  </ol>
                )}
              </div>
            </div>
          </section>
        );

      default:
        return wrap(
          <>
            <Kicker>{kicker}</Kicker>
            <h2>{heading}</h2>
            <div className="pp-card pp-simple">
              <Body value={typeof c === "string" ? c : c.text} />
              {list(c.items).filter(rowHasText).length > 0 && (
                <ul className="pp-ticks">
                  {list(c.items).filter(rowHasText).map((x: any, i: number) => <li key={i}>{rowText(x)}</li>)}
                </ul>
              )}
            </div>
          </>
        );
    }
  };

  const cc = cover?.content || {};
  const ledes = [txt(cc.subheadline), ...list(cc.ledes).map(txt)].filter(Boolean);
  const badges = list(cc.badges).filter(rowHasText);

  return (
    <div className={`pp-root ${mode === "preview" ? "is-preview" : ""}`}>
      <style>{CSS}</style>

      <div className="pp-shell">
        {/* Context bar — exactly the reference's "You're viewing a proposal for …" */}
        <div className="pp-context-bar">
          <div className="pp-wrap pp-context-inner">
            <div className="pp-context-job">
              <span>
                You&apos;re viewing a proposal for <b>{txt(p.title)}</b>
              </span>
            </div>
            {navItems.length > 1 && (
              <nav className="pp-nav-pills">
                {navItems.map((s: any) => (
                  <a
                    key={s.id}
                    href={`#pp-${s.id}`}
                    className={active === s.id ? "is-active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(`pp-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    {NAV_LABEL[s.type] || txt(s.title) || s.type}
                  </a>
                ))}
              </nav>
            )}
          </div>
        </div>

        {/* Hero — headline, ledes, credential rings, portfolio spotlight */}
        <div className="pp-wrap pp-hero">
          <div className={`pp-hero-inner ${pfItems.length ? "" : "is-solo"}`}>
            <div>
              {txt(cc.eyebrow) && <Kicker>{cc.eyebrow}</Kicker>}
              <h1 className="pp-headline">{txt(cc.headline) || txt(p.title)}</h1>
              {ledes.map((l, i) => (
                <p className="pp-lede" key={i}>{l}</p>
              ))}
              {badges.length > 0 && (
                <div className="pp-stat-row">
                  {badges.map((x: any, i: number) => (
                    <div className="pp-stat" key={i}>
                      <div className="pp-ring">
                        <div className="pp-ring-inner">{txt(x.value)}</div>
                      </div>
                      <div className="pp-stat-label">
                        <b>{txt(x.label)}</b>
                        {txt(x.sub)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Who the client is actually talking to — the freelance profile's
                  own picture and name, right under the credentials. */}
              {signer && (
                <div className="pp-freelancer">
                  {who?.profilePic ? (
                    <img src={who.profilePic} alt={signer} />
                  ) : (
                    <span className="pp-freelancer-initial">{signer.slice(0, 1).toUpperCase()}</span>
                  )}
                  <div>
                    <b>{signer}</b>
                    {credentials && <span>{credentials}</span>}
                  </div>
                </div>
              )}
            </div>

            {pfItems.length > 0 && (
              <div className="pp-hero-proof">
                <PortfolioCard />
              </div>
            )}
          </div>
        </div>

        {sections.map(renderSection)}

        <footer className="pp-footer">
          <div className="pp-wrap">
            <p>{[signer, credentials].filter(Boolean).join(" — ")}</p>
          </div>
        </footer>
      </div>

      {/* Sticky close — ghost link to pricing plus the primary ask */}
      {(ctaUrl || featuredTier) && (
        <div className="pp-sticky-cta">
          <div className="pp-wrap pp-sticky-inner">
            <span className="pp-sticky-text">
              {featuredTier ? (
                <>
                  Ready when you are —{" "}
                  <b>
                    {packageLabel(featuredTier)}
                    {txt(featuredTier.price)
                      ? `, ${money(featuredTier.price, pricing?.content?.currency || "USD")}`
                      : ""}
                  </b>
                </>
              ) : (
                <>Prepared for <b>{clientName || txt(p.title)}</b></>
              )}
            </span>
            <div className="pp-sticky-actions">
              {pricing && (
                <a
                  className="pp-btn pp-btn-ghost"
                  href={`#pp-${pricing.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`pp-${pricing.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  See pricing
                </a>
              )}
              {ctaUrl ? (
                <a className="pp-btn" href={ctaUrl} target="_blank" rel="noreferrer">{ctaLabel}</a>
              ) : (
                <span className="pp-btn pp-btn-static">{ctaLabel}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Portfolio carousel */}
      {modalOpen && pfItems[modalIdx!] && (
        <div className="pp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setModalIdx(null)}>
          <div className="pp-modal">
            <div className="pp-modal-head">
              <h3>{pfLabel}</h3>
              <button className="pp-modal-close" onClick={() => setModalIdx(null)} aria-label="Close">×</button>
            </div>

            <div className="pp-frame-swatch">
              {txt(pfItems[modalIdx!].image) ? (
                <img src={pfItems[modalIdx!].image} alt={txt(pfItems[modalIdx!].title)} />
              ) : (
                <span className="pp-pf-initial">{(txt(pfItems[modalIdx!].title) || "·").slice(0, 1)}</span>
              )}
            </div>
            <div className="pp-frame-info">
              <p className="pp-pf-name">{txt(pfItems[modalIdx!].title)}</p>
              {txt(pfItems[modalIdx!].meta || pfItems[modalIdx!].description) && (
                <p className="pp-pf-meta">{txt(pfItems[modalIdx!].meta || pfItems[modalIdx!].description)}</p>
              )}
            </div>

            <div className="pp-nav-row">
              <button className="pp-nav-btn" disabled={modalIdx === 0} onClick={() => stepModal(-1)}>← Previous</button>
              <span className="pp-count">{(modalIdx || 0) + 1} of {pfItems.length}</span>
              <button className="pp-nav-btn is-next" onClick={() => stepModal(1)}>
                {modalIdx === pfItems.length - 1 ? "Restart" : "Next →"}
              </button>
            </div>

            <div className="pp-dots">
              {pfItems.map((_: any, i: number) => (
                <button
                  key={i}
                  className={`pp-dot ${i === modalIdx ? "is-active" : ""}`}
                  aria-label={`Go to project ${i + 1}`}
                  onClick={() => setModalIdx(i)}
                />
              ))}
            </div>

            {pfNote && <p className="pp-modal-note">{pfNote}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * The reference stylesheet, scoped to .pp-root.
 *
 * Fixed light palette on purpose: a proposal must read identically for every
 * client, whatever their device theme is set to. Only --pp-accent (and the
 * shades derived from it) follows the brand.
 */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.pp-root{
  color-scheme: light;
  /* The reference palette, fixed. No tenant colour is applied here: the client
     receives this under the freelance profile, not under an agency brand. */
  --pp-accent:#108A00; --pp-accent-deep:#0C6B00; --pp-accent-soft:#E9F6E5; --pp-accent-line:#BFE3B5;
  --pp-sans:'Inter', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
  --pp-ink:#1B1B1D; --pp-ink-soft:#53565C; --pp-ink-faint:#767A82;
  --pp-bg:#F6F7F9; --pp-card:#FFFFFF; --pp-line:#E3E5E9; --pp-star:#F5A623;
  --pp-shadow: 0 1px 2px rgba(20,20,24,0.06), 0 1px 1px rgba(20,20,24,0.04);
  background: var(--pp-bg); color: var(--pp-ink);
  font-family: var(--pp-sans); font-size:15.5px; line-height:1.55;
  -webkit-font-smoothing: antialiased; padding-bottom:76px;
}
.pp-root *, .pp-root *::before, .pp-root *::after{ box-sizing:border-box; }
.pp-root img, .pp-root svg{ max-width:100%; display:block; }
.pp-root a{ color:inherit; text-decoration:none; }
.pp-root p{ margin:0; }
.pp-root h1, .pp-root h2, .pp-root h3{ font-family:var(--pp-sans); font-weight:800; margin:0; color:var(--pp-ink); letter-spacing:-0.01em; }
.pp-root button{ font-family:var(--pp-sans); }
.pp-shell{ container-type: inline-size; container-name: pp; }
.pp-wrap{ max-width:1080px; margin:0 auto; padding:0 32px; }
@container pp (max-width:640px){ .pp-wrap{ padding:0 20px; } }
.pp-card{ background:var(--pp-card); border:1px solid var(--pp-line); border-radius:12px; box-shadow:var(--pp-shadow); }

/* Context bar */
.pp-context-bar{ background:var(--pp-card); border-bottom:1px solid var(--pp-line); padding:14px 0; position:sticky; top:0; z-index:20; }
.pp-root.is-preview .pp-context-bar{ position:static; }
.pp-context-inner{ display:flex; align-items:center; justify-content:space-between; gap:20px; flex-wrap:nowrap; }
.pp-context-job{ min-width:0; flex:1 1 auto; font-size:13.5px; color:var(--pp-ink-faint); font-weight:600; }
/* A long job title shortens rather than pushing the pills onto a second row. */
.pp-context-job span{ display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pp-context-job b{ color:var(--pp-ink); font-weight:700; }
.pp-nav-pills{ display:flex; gap:6px; background:var(--pp-bg); border:1px solid var(--pp-line); border-radius:100px; padding:4px; flex:0 0 auto; }
.pp-nav-pills a{ font-size:13px; font-weight:700; color:var(--pp-ink-soft); padding:7px 14px; border-radius:100px; white-space:nowrap; }
.pp-nav-pills a.is-active{ background:var(--pp-accent); color:#fff; }
@container pp (max-width:700px){ .pp-nav-pills{ overflow-x:auto; } .pp-context-job span{ display:none; } }

/* Hero */
.pp-hero{ padding:26px 0 52px; }
.pp-hero-inner{ display:grid; grid-template-columns:1.05fr 0.95fr; gap:44px; align-items:start; }
.pp-hero-inner.is-solo{ grid-template-columns:1fr; }
@container pp (max-width:900px){ .pp-hero-inner{ grid-template-columns:1fr; gap:28px; } }
.pp-headline{ font-size:clamp(26px,3.6vw,36px); line-height:1.25; }
.pp-lede{ margin-top:16px; font-size:16px; color:var(--pp-ink-soft); max-width:52ch; }
.pp-stat-row{ display:flex; gap:24px; margin-top:28px; flex-wrap:wrap; }
.pp-stat{ display:flex; align-items:center; gap:10px; }
.pp-ring{ width:46px; height:46px; border-radius:50%; display:flex; align-items:center; justify-content:center;
  background:conic-gradient(var(--pp-accent) 100%, var(--pp-line) 0); flex:0 0 auto; }
.pp-ring-inner{ width:36px; height:36px; border-radius:50%; background:var(--pp-card); display:flex; align-items:center; justify-content:center;
  font-size:10.5px; font-weight:800; color:var(--pp-ink); text-align:center; line-height:1.1; padding:2px; }
.pp-stat-label{ font-size:12.5px; color:var(--pp-ink-faint); font-weight:600; line-height:1.3; }
.pp-stat-label b{ display:block; color:var(--pp-ink); font-size:13.5px; font-weight:800; }

/* Portfolio spotlight */
.pp-portfolio-card{ padding:16px 16px 18px; }
.pp-portfolio-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.pp-portfolio-head span{ font-size:12px; font-weight:700; color:var(--pp-ink-faint); text-transform:uppercase; letter-spacing:.03em; }
.pp-pf-tabs{ display:flex; gap:6px; background:var(--pp-bg); border:1px solid var(--pp-line); border-radius:100px; padding:3px; margin-bottom:14px; }
.pp-pf-tab{ flex:1; text-align:center; font-size:12.5px; font-weight:700; color:var(--pp-ink-soft); padding:8px 6px; border-radius:100px; cursor:pointer; border:none; background:none; }
.pp-pf-tab.is-active{ background:var(--pp-accent); color:#fff; }
.pp-pf-solo{ font-size:13px; font-weight:700; margin-bottom:14px; }
.pp-pf-panel{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.pp-pf-tile{ border:1px solid var(--pp-line); border-radius:10px; overflow:hidden; background:var(--pp-bg); cursor:pointer; transition:transform .12s ease; padding:0; text-align:left; }
.pp-pf-tile:hover{ transform:translateY(-2px); }
.pp-pf-swatch{ aspect-ratio:1.15; display:flex; align-items:center; justify-content:center; overflow:hidden; background:#fff; }
/* Images keep their own proportions — shown whole, never cropped to fill. */
.pp-pf-swatch img{ width:100%; height:100%; object-fit:contain; padding:8px; }
.pp-pf-initial{ font-size:26px; font-weight:800; color:var(--pp-ink-faint); }
.pp-pf-info{ padding:9px 10px 11px; background:var(--pp-card); }
.pp-pf-name{ font-size:11.5px; font-weight:700; color:var(--pp-ink); margin:0; }
.pp-pf-meta{ font-size:10.5px; color:var(--pp-ink-faint); margin:2px 0 0; }
.pp-pf-seeall{ display:block; margin-top:12px; font-size:12.5px; font-weight:700; color:var(--pp-accent-deep); cursor:pointer; text-align:left; background:none; border:none; padding:0; }
.pp-pf-note{ font-size:11.5px; color:var(--pp-ink-faint); margin-top:12px; line-height:1.4; }

/* The freelance profile, under the credential rings */
.pp-freelancer{ display:flex; align-items:center; gap:12px; margin-top:26px; padding-top:22px; border-top:1px solid var(--pp-line); }
.pp-freelancer img{ width:48px; height:48px; border-radius:50%; object-fit:cover; flex:0 0 auto; }
.pp-freelancer-initial{ width:48px; height:48px; border-radius:50%; background:var(--pp-accent-soft); border:1px solid var(--pp-accent-line);
  color:var(--pp-accent-deep); display:flex; align-items:center; justify-content:center; font-weight:800; font-size:18px; flex:0 0 auto; }
.pp-freelancer b{ display:block; font-size:15px; font-weight:800; color:var(--pp-ink); }
.pp-freelancer span{ font-size:12.5px; color:var(--pp-ink-faint); font-weight:600; }


/* Sections */
.pp-section{ padding:44px 0; }
.pp-section h2, .pp-pricing-band h2, .pp-closing h2{ font-size:clamp(20px,2.2vw,25px); max-width:26ch; }
.pp-kicker{ display:inline-flex; align-items:center; gap:6px; font-size:12.5px; font-weight:700; color:var(--pp-accent-deep);
  background:var(--pp-accent-soft); border:1px solid var(--pp-accent-line); padding:5px 12px; border-radius:100px; margin:0 0 14px; }
.pp-soft{ color:var(--pp-ink-soft); font-size:14.5px; }
.pp-lede-block{ margin-top:16px; color:var(--pp-ink-soft); font-size:15.5px; max-width:64ch; }
.pp-lede-block p + p{ margin-top:12px; }
.pp-rich p{ margin:0 0 10px; }
.pp-rich ul{ margin:10px 0; padding-left:20px; }

.pp-understood{ padding:28px 30px; display:grid; grid-template-columns:0.8fr 1.2fr; gap:32px; border-left:3px solid var(--pp-accent); margin-top:0; }
@container pp (max-width:800px){ .pp-understood{ grid-template-columns:1fr; gap:16px; } }
.pp-understood p{ color:var(--pp-ink-soft); font-size:15.5px; max-width:56ch; }
.pp-understood p + p{ margin-top:12px; }
.pp-callout{ margin-top:16px; padding:12px 14px; border-radius:9px; background:var(--pp-accent-soft); border:1px solid var(--pp-accent-line);
  color:var(--pp-accent-deep); font-weight:600; font-size:14.5px; }
.pp-mini-stats{ display:flex; gap:20px; flex-wrap:wrap; margin-top:18px; }
.pp-mini-stats-wide{ margin:22px 0 4px; gap:28px; }
.pp-mini-stats b{ display:block; font-size:19px; font-weight:800; color:var(--pp-accent-deep); }
.pp-mini-stats span{ font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--pp-ink-faint); }

.pp-steps{ list-style:none; margin:28px 0 0; padding:0; display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
@container pp (max-width:800px){ .pp-steps{ grid-template-columns:1fr; } }
.pp-steps li{ padding:20px 18px; }
.pp-num{ display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px; border-radius:6px;
  background:var(--pp-accent-soft); border:1px solid var(--pp-accent-line); color:var(--pp-accent-deep); font-weight:800; font-size:13px; margin-bottom:12px; }
.pp-steps h3{ font-size:15.5px; margin-bottom:6px; }
.pp-steps p{ font-size:13.5px; color:var(--pp-ink-soft); margin:0; }
.pp-duration{ font-size:12px !important; font-weight:800; color:var(--pp-accent-deep) !important; margin-bottom:4px !important; }

.pp-grid2{ display:grid; grid-template-columns:repeat(2,1fr); gap:14px; margin-top:28px; }
.pp-grid3{ display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-top:28px; }
@container pp (max-width:900px){ .pp-grid3{ grid-template-columns:repeat(2,1fr); } }
@container pp (max-width:640px){ .pp-grid2, .pp-grid3{ grid-template-columns:1fr; } }
.pp-simple{ padding:20px 18px; }
.pp-simple h3{ font-size:15.5px; margin-bottom:6px; }
.pp-simple p{ font-size:13.5px; color:var(--pp-ink-soft); }
.pp-inline-price{ margin-top:10px !important; font-size:14px !important; font-weight:800; color:var(--pp-accent-deep) !important; }

.pp-deliverables{ padding:24px 26px; margin-top:28px; }
.pp-ticks{ list-style:none; margin:14px 0 0; padding:0; }
.pp-ticks li{ position:relative; padding:7px 0 7px 22px; font-size:14px; color:var(--pp-ink-soft); }
.pp-ticks li b{ color:var(--pp-ink); font-weight:700; }
.pp-ticks li::before{ content:""; position:absolute; left:0; top:12px; width:12px; height:12px; background:var(--pp-accent);
  -webkit-mask:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23000' d='M6.2 11.6 2.9 8.3l1.1-1.1 2.2 2.2 5.7-5.7 1.1 1.1z'/%3E%3C/svg%3E") center/contain no-repeat;
  mask:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23000' d='M6.2 11.6 2.9 8.3l1.1-1.1 2.2 2.2 5.7-5.7 1.1 1.1z'/%3E%3C/svg%3E") center/contain no-repeat; }
.pp-format{ display:inline-block; margin-left:8px; font-style:normal; font-size:10.5px; font-weight:800; text-transform:uppercase; letter-spacing:.04em;
  color:var(--pp-accent-deep); background:var(--pp-accent-soft); border:1px solid var(--pp-accent-line); border-radius:100px; padding:2px 8px; }
.pp-excluded{ margin-top:18px; border:1px dashed var(--pp-line); border-radius:10px; padding:14px 16px; }
.pp-excluded span{ font-size:11.5px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; color:var(--pp-ink-faint); }
.pp-excluded ul{ margin:8px 0 0; padding-left:20px; font-size:13.5px; color:var(--pp-ink-soft); }

/* Pricing band */
.pp-pricing-band{ background:var(--pp-accent-soft); border-top:1px solid var(--pp-accent-line); border-bottom:1px solid var(--pp-accent-line); }
.pp-tiers{ display:grid; grid-template-columns:repeat(3,1fr); gap:18px; margin-top:26px; }
@container pp (max-width:800px){ .pp-tiers{ grid-template-columns:1fr; } }
.pp-tier{ padding:24px 22px; display:flex; flex-direction:column; position:relative; }
.pp-tier.is-featured{ border-color:var(--pp-accent); box-shadow:0 0 0 2px var(--pp-accent-line), var(--pp-shadow); }
.pp-flag{ position:absolute; top:-11px; left:22px; background:var(--pp-accent); color:#fff; font-size:11px; font-weight:800; padding:4px 10px; border-radius:5px; }
.pp-tier-tag{ font-size:12px; color:var(--pp-ink-faint); font-weight:700; text-transform:uppercase; letter-spacing:.03em; margin-bottom:6px; }
.pp-tier h3{ font-size:16.5px; margin-bottom:6px; }
.pp-price{ font-size:28px; font-weight:800; margin:8px 0 16px; color:var(--pp-ink); }
.pp-price small{ font-size:13px; color:var(--pp-ink-faint); font-weight:600; }
.pp-price-tba{ font-size:18px; color:var(--pp-ink-faint); font-weight:700; }
.pp-tier ul{ list-style:none; padding:0; margin:0 0 18px; flex:1; }
.pp-tier li{ font-size:13.5px; color:var(--pp-ink-soft); padding:7px 0 7px 22px; position:relative; }
.pp-tier li::before{ content:""; position:absolute; left:0; top:11px; width:12px; height:12px; background:var(--pp-accent);
  -webkit-mask:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23000' d='M6.2 11.6 2.9 8.3l1.1-1.1 2.2 2.2 5.7-5.7 1.1 1.1z'/%3E%3C/svg%3E") center/contain no-repeat;
  mask:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23000' d='M6.2 11.6 2.9 8.3l1.1-1.1 2.2 2.2 5.7-5.7 1.1 1.1z'/%3E%3C/svg%3E") center/contain no-repeat; }
.pp-tier-note{ font-size:12.5px; color:var(--pp-ink-faint); margin-top:auto; }
.pp-band-note{ margin-top:18px; font-size:12.5px; color:var(--pp-ink-faint); }

/* Quotes */
.pp-quotes{ display:grid; grid-template-columns:repeat(3,1fr); gap:18px; margin-top:26px; }
@container pp (max-width:800px){ .pp-quotes{ grid-template-columns:1fr; } }
.pp-quote{ padding:22px 20px; }
.pp-stars{ color:var(--pp-star); font-size:14px; margin-bottom:10px; letter-spacing:1px; }
.pp-quote p{ font-size:15px; line-height:1.5; color:var(--pp-ink); margin:0 0 12px; }
.pp-attr{ font-size:12.5px !important; color:var(--pp-ink-faint); font-weight:600; margin:0 !important; }

/* Closing + footer */
.pp-closing{ padding:44px 0 8px; }
.pp-closing .pp-card{ padding:32px 30px; }
.pp-closing p{ color:var(--pp-ink-soft); font-size:15px; margin-top:10px; max-width:50ch; }
.pp-next-list{ margin:14px 0 0; padding-left:20px; color:var(--pp-ink-soft); font-size:14px; }
.pp-next-list li{ padding:3px 0; }
.pp-footer{ padding:24px 0 90px; text-align:center; }
.pp-footer p{ font-size:12.5px; color:var(--pp-ink-faint); margin:0; }
.pp-footer p + p{ margin-top:5px; }

/* Sticky CTA */
.pp-sticky-cta{ position:fixed; left:0; right:0; bottom:0; z-index:30; background:var(--pp-card); border-top:1px solid var(--pp-line);
  padding:12px 0; box-shadow:0 -4px 14px rgba(20,20,24,0.08); }
.pp-root.is-preview .pp-sticky-cta{ position:sticky; }
.pp-sticky-inner{ display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.pp-sticky-text{ font-size:13.5px; color:var(--pp-ink-soft); font-weight:600; }
.pp-sticky-text b{ color:var(--pp-ink); }
.pp-sticky-actions{ display:flex; gap:10px; }
.pp-btn{ display:inline-block; background:var(--pp-accent); color:#fff; font-weight:700; font-size:14px; padding:11px 22px; border-radius:8px; }
.pp-btn-ghost{ background:transparent; color:var(--pp-accent-deep); border:1px solid var(--pp-accent-line); }
.pp-btn-static{ opacity:.85; }
@container pp (max-width:600px){ .pp-sticky-text{ display:none; } }

/* Portfolio modal */
.pp-modal-overlay{ position:fixed; inset:0; background:rgba(20,20,24,0.55); display:flex; align-items:center; justify-content:center; z-index:100; padding:24px; }
.pp-modal{ background:var(--pp-card); border-radius:14px; max-width:420px; width:100%; max-height:88vh; overflow-y:auto; padding:22px 24px 24px; box-shadow:0 20px 50px rgba(0,0,0,0.3); }
.pp-modal-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.pp-modal-head h3{ font-size:16.5px; }
.pp-modal-close{ width:30px; height:30px; border-radius:8px; border:1px solid var(--pp-line); background:var(--pp-bg); color:var(--pp-ink-soft); font-size:15px; cursor:pointer; }
.pp-frame-swatch{ aspect-ratio:1.3; border-radius:12px; display:flex; align-items:center; justify-content:center; border:1px solid var(--pp-line); background:var(--pp-bg); overflow:hidden; }
.pp-frame-swatch img{ width:100%; height:100%; object-fit:contain; padding:10px; }
.pp-frame-info{ margin-top:14px; text-align:center; }
.pp-frame-info .pp-pf-name{ font-size:15px; font-weight:800; }
.pp-frame-info .pp-pf-meta{ font-size:12.5px; margin-top:4px; }
.pp-nav-row{ display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:16px; }
.pp-nav-btn{ display:flex; align-items:center; gap:6px; font-size:13px; font-weight:700; color:var(--pp-ink-soft); background:var(--pp-bg);
  border:1px solid var(--pp-line); border-radius:8px; padding:9px 14px; cursor:pointer; }
.pp-nav-btn:disabled{ opacity:.4; cursor:default; }
.pp-nav-btn.is-next{ color:#fff; background:var(--pp-accent); border-color:var(--pp-accent); }
.pp-count{ font-size:12.5px; color:var(--pp-ink-faint); font-weight:600; }
.pp-dots{ display:flex; justify-content:center; gap:7px; margin-top:16px; flex-wrap:wrap; }
.pp-dot{ width:9px; height:9px; border-radius:50%; background:var(--pp-line); border:none; cursor:pointer; padding:0; }
.pp-dot.is-active{ background:var(--pp-accent); width:22px; border-radius:5px; }
.pp-modal-note{ font-size:12px; color:var(--pp-ink-faint); margin-top:16px; text-align:center; }
`;

export default InfographicTemplate;
